import React from "react";
import {
  getCardSummaryContent,
  RemoteCruiseCardContent,
  RemoteCruiseCardContent_Suite
} from "../../templates/RequestAQuote/utils";
import * as styles from "./CruiseCardSummary.module.scss";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import { isPresent } from "src/utils/checks";
import {
  cruisePriceToPrices,
  suitePricestoPrices,
  withLowestFareAvailableLabel
} from "components/Prices/utils";
import { Icon, SanityImage } from "@silversea-ssc/ui-components-react";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { Trans, useLingui } from "@lingui/react/macro";
import Dates from "components/CruiseCard/Dates/Dates";
import { toUTCDate } from "src/utils/date";
import PriceList from "components/Prices/PriceList/PriceList";

interface CruiseCardSummaryProps {
  cruise?: RemoteCruiseCardContent["cruise"];
  specialVoyage?: RemoteCruiseCardContent["specialVoyage"];
  suite?: RemoteCruiseCardContent_Suite;
  suiteCategoryCode?: string;
  prices?: CruisePriceAndOffersV2Response;
  cruiseCode: string | undefined;
}

const CuiseCardSummary: React.FC<
  React.PropsWithChildren<CruiseCardSummaryProps>
> = ({
  cruise,
  specialVoyage,
  suite,
  suiteCategoryCode,
  prices,
  cruiseCode
}) => {
  const { t } = useLingui();
  const geolocation = useGeolocation();
  const { title, image } = getCardSummaryContent(cruise, specialVoyage);

  const voyage = cruise || specialVoyage;
  const { departureDate, arrivalDate, ship } = voyage?.data || {};
  const departureDateUTC = departureDate ? toUTCDate(departureDate) : undefined;
  const arrivalDateUTC = arrivalDate ? toUTCDate(arrivalDate) : undefined;
  const cruisePrice = cruisePriceToPrices(
    t,
    prices,
    geolocation.defaultCurrency
  );
  const pricesForSuite =
    suiteCategoryCode && isPresent(prices?.suitePrices)
      ? suitePricestoPrices(
          t,
          [suiteCategoryCode],
          prices.suitePrices,
          geolocation.defaultCurrency,
          true,
          "without-from"
        )
      : undefined;

  const transformedPrices = (pricesForSuite?.prices || cruisePrice).filter(
    isPresent
  );
  const withSuiteCategory = isPresent(pricesForSuite?.prices);

  return (
    <article className={styles.card}>
      <div className={styles.imageContainer}>
        <SanityImage
          image={image}
          width={400}
          height={140}
          className={styles.backgroundImage}
        />

        <div className={styles.content}>
          <div className={styles.name}>{title}</div>

          <Dates
            departureDate={departureDateUTC}
            arrivalDate={arrivalDateUTC}
            durationDays={voyage?.data?.days}
            separator="bullet"
          />

          <div className={styles.ship}>
            <Icon name="ship" padding={"inline-right"} />{" "}
            <span>{ship?.name}</span> |{" "}
            <Trans>Voyage number #{cruiseCode}</Trans>
          </div>
        </div>
      </div>
      <div className={styles.extraContent}>
        {suite && (
          <div className={styles.suiteName}>
            {suite.category?.name?.localized}
          </div>
        )}

        {transformedPrices && (
          <div>
            <div
              className={styles.perGuest}
            >{t`Prices per guest start from`}</div>
            <PriceList prices={transformedPrices} chipBackgroundColor="white" />
            {!withSuiteCategory && (
              <div className={styles.lowestFare}>
                {t(withLowestFareAvailableLabel())}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default CuiseCardSummary;
